import {
  type RefObject,
  useCallback,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import useResizeObserver from "@react-hook/resize-observer";
import { throttle as throttleFn } from "es-toolkit/function";

type Size = DOMRectReadOnly | { width: number; height: number };

const EMPTY_OBJ = {} as DOMRectReadOnly;
const BODY_REF = { current: document.body } as const;

export const useSize = (
  target: RefObject<Element | null> | Element | null = BODY_REF,
  { throttle }: { throttle?: number } = {}
): Size => {
  const [size, setSize] = useState({} as Size);
  const throttledSetSize = useMemo(
    () => throttleFn(setSize, throttle ?? 0),
    [throttle]
  );
  const element = target instanceof Element ? target : target?.current;

  useLayoutEffect(() => {
    const clientRect = element?.getBoundingClientRect?.();

    setSize(clientRect || EMPTY_OBJ);
  }, [element]);

  const observerCB = useCallback(
    (entry: ResizeObserverEntry) => {
      const newSize = {
        // ...entry.contentRect.toJSON(),
        width: entry.borderBoxSize[0].inlineSize,
        height: entry.borderBoxSize[0].blockSize,
      };

      if (throttle) {
        throttledSetSize(newSize);
      } else {
        setSize(newSize);
      }
    },
    [throttle, throttledSetSize]
  );

  // Where the magic happens
  useResizeObserver(target, observerCB);

  return size;
};
